/**
 * Reset & Regulate — 30-Day Nervous System Regulation Journey — programme content.
 *
 * Structured from the Recovery Trauma™ somatic exercise material and the
 * nervous-system regulation workbook, rewritten in cautious, trauma-informed
 * language. Nothing here claims to cure, heal or "reset" anything: practices
 * are invitations, every step is optional and stillness is always allowed.
 */

export const NSR_SLUG = "nervous-system-reset-30";

export type StageKey = "notice" | "move" | "discover" | "real-life";

export interface Stage {
  key: StageKey;
  label: string;
  range: [number, number];
  blurb: string;
}

export const STAGES: Stage[] = [
  {
    key: "notice",
    label: "Notice Your System",
    range: [1, 7],
    blurb:
      "Learn to recognise your own patterns, body signals and nervous-system responses.",
  },
  {
    key: "move",
    label: "Move & Regulate",
    range: [8, 14],
    blurb:
      "Gentle, body-based practices. Slow movement, comfort and choice — never force.",
  },
  {
    key: "discover",
    label: "Build Safety",
    range: [15, 21],
    blurb:
      "Practise the kinds of regulation that help your body feel steadier and safer.",
  },
  {
    key: "real-life",
    label: "Find What Works for You",
    range: [22, 30],
    blurb:
      "Use what you've learned in everyday moments and build your personal toolkit.",
  },
];

export function stageForDay(day: number): Stage {
  return STAGES.find((s) => day >= s.range[0] && day <= s.range[1]) ?? STAGES[0];
}

/* ------------------------------ Check-in ------------------------------ */

export const STATE_OPTIONS = [
  { value: "activated", emoji: "🔥", label: "Activated / On Edge" },
  { value: "anxious", emoji: "🌀", label: "Anxious / Restless" },
  { value: "frozen", emoji: "🧊", label: "Frozen / Shut Down" },
  { value: "numb", emoji: "🌫", label: "Numb / Disconnected" },
  { value: "exhausted", emoji: "😴", label: "Exhausted" },
  { value: "calm", emoji: "🌿", label: "Calm / Connected" },
  { value: "unsure", emoji: "❓", label: "I'm Not Sure" },
];

export const BODY_NOTICE_OPTIONS = [
  "Jaw",
  "Neck",
  "Shoulders",
  "Chest",
  "Stomach",
  "Hands",
  "Legs",
  "Head",
  "Breathing",
  "Heart rate",
  "Restlessness",
  "Heaviness",
  "Numbness",
  "Whole body",
  "I don't notice anything right now",
];

export const CAPACITY_OPTIONS = [
  { value: "ready", label: "I'm ready for today's practice" },
  { value: "gentler", label: "I'd like something gentler" },
  { value: "checkin", label: "I just want to check in" },
  { value: "skip", label: "Skip today" },
];

export const AFTER_OPTIONS = [
  "More settled",
  "More activated",
  "More present",
  "More tired",
  "No noticeable change",
  "Something else",
  "I'm not sure",
];

export const KEEP_OPTIONS = [
  { value: "yes", label: "♡ Yes — add to my toolkit" },
  { value: "maybe", label: "Maybe" },
  { value: "no", label: "Not for me" },
];

/* --------------------------- Optional profile --------------------------- */

export const PROFILE_STRESS_RESPONSES = [
  "Overthink",
  "Become restless",
  "Feel anxious",
  "Feel angry",
  "Want to escape",
  "Freeze",
  "Shut down",
  "Feel numb",
  "Disconnect from my body",
  "People-please",
  "Seek reassurance",
  "Become hyper-alert",
  "Feel exhausted",
  "I'm not sure",
];

export const PROFILE_BODY_AREAS = [
  "Jaw",
  "Neck",
  "Shoulders",
  "Chest",
  "Stomach",
  "Hands",
  "Legs",
  "Head",
  "Whole body",
  "I don't notice it physically",
];

export const PROFILE_HELPS = [
  "Movement",
  "Being outside",
  "Music or sound",
  "Warmth",
  "Rest",
  "Talking to someone",
  "Being alone",
  "Writing",
  "Breathing slowly",
  "Grounding",
  "Animals",
  "Water",
  "I'm not sure yet",
];

export const PROFILE_HARDER = [
  "Not enough sleep",
  "Noise or crowds",
  "Conflict",
  "Being rushed",
  "Feeling watched",
  "Scrolling",
  "Hunger",
  "Isolation",
  "Certain places",
  "Certain people",
  "I'm not sure yet",
];

/* ------------------------------ Day content ------------------------------ */

export interface Picker {
  id: string;
  question: string;
  options: string[];
  multi?: boolean;
  allowCustom?: boolean;
  /** Where an answer belongs in the final toolkit, if anywhere. */
  toolkitKey?: ToolkitKey;
  note?: string;
}

export type ToolkitKey =
  | "safetyCues"
  | "whenActivated"
  | "whenDisconnected"
  | "beforeBed"
  | "grounding"
  | "movement"
  | "breathing"
  | "sounds"
  | "morning"
  | "evening"
  | "goTo";

export interface ToolLink {
  to: string;
  label: string;
  desc: string;
}

export interface Practice {
  title: string;
  minutes: string;
  purpose: string;
  steps: string[];
  tips?: string[];
  seatedAlternative?: string;
  /** Shown when someone chooses "something gentler" at check-in. */
  gentler?: string;
}

export interface Day {
  day: number;
  title: string;
  intro: string[];
  practice?: Practice;
  pickers?: Picker[];
  reflection?: string;
  reflectionOptions?: string[];
  journalPrompt?: string;
  tools?: ToolLink[];
  optional?: string;
}

const SAFETY_NOTE =
  "Move slowly and stay within a comfortable range. Smaller movements are enough, stillness is welcome, and you can stop at any point. Use a chair, wall or cushion for support if that helps.";

export const PRACTICE_SAFETY_NOTE = SAFETY_NOTE;

export const DAYS: Day[] = [
  /* ------------------------ Stage 1 — Notice ------------------------ */
  {
    day: 1,
    title: "Meet Your Nervous System",
    intro: [
      "Your nervous system is always working in the background, reading your surroundings and adjusting how much energy you have available.",
      "Under stress, people notice different responses. Some feel wound up or on edge. Some feel flat, heavy or far away. Some move between the two in a single day. None of these is a wrong state, and none of them means something is broken in you.",
      "Over the next 30 days you'll be doing two things: noticing what happens in your body, and gently exploring what seems to help. That's all. There's nothing to get right.",
    ],
    pickers: [
      {
        id: "familiar",
        question: "Which of these feels most familiar to you at the moment?",
        options: [
          "Wound up or on edge",
          "Flat, heavy or far away",
          "Moving between both",
          "Mostly settled",
          "I'm not sure yet",
        ],
      },
    ],
    reflection:
      "What made you want to start these 30 days? A sentence is plenty.",
    tools: [
      {
        to: "/learn/polyvagal-ladder",
        label: "Polyvagal Ladder",
        desc: "A gentle explainer on nervous-system states.",
      },
    ],
  },
  {
    day: 2,
    title: "What State Am I In?",
    intro: [
      "Today is simply about naming where you are right now — not diagnosing yourself, and not deciding whether it's good or bad.",
      "States change through the day. Whatever you notice now is just information about this moment.",
    ],
    pickers: [
      {
        id: "state-now",
        question: "Which of these feels closest right now?",
        options: STATE_OPTIONS.map((s) => `${s.emoji} ${s.label}`),
        allowCustom: true,
      },
      {
        id: "state-frequency",
        question: "Which state have you spent most time in this week?",
        options: STATE_OPTIONS.map((s) => `${s.emoji} ${s.label}`),
      },
    ],
    reflection:
      "If your current state could speak, what might it be asking for?",
  },
  {
    day: 3,
    title: "Your Body's Signals",
    intro: [
      "Take a slow scan through your body. You're only noticing — there's no need to change anything, and no need to work out what a sensation means.",
      "If scanning the body feels uncomfortable, you can keep your eyes open, notice only one area, or stop.",
    ],
    practice: {
      title: "Gentle body-awareness scan",
      minutes: "3–5 minutes",
      purpose:
        "Build awareness of where you tend to hold sensation, without interpreting it.",
      steps: [
        "Sit or lie in a position that feels supported.",
        "Let your attention rest on your jaw for a few breaths. Just notice.",
        "Move slowly through the shoulders, chest, stomach and hands.",
        "Notice your breathing and heart rate as they are — no need to slow them.",
        "Notice any restlessness, heaviness or numbness without labelling it.",
        "Finish by feeling your feet or your seat against the surface below you.",
      ],
      tips: [
        "You can skip any area that feels too much today.",
        "Eyes open is completely fine.",
      ],
      gentler: "Notice one area only — your hands — for thirty seconds.",
    },
    pickers: [
      {
        id: "signals",
        question: "Where did you notice something?",
        options: BODY_NOTICE_OPTIONS,
        multi: true,
        allowCustom: true,
      },
    ],
    reflection: "Which area asked for the most attention today?",
  },
  {
    day: 4,
    title: "Find Your Safety Cues",
    intro: [
      "Safety cues are the small, ordinary things that help you feel a little more grounded or supported — a place, a person, a sound, a texture, a routine.",
      "They're personal. What settles one person can unsettle another, so trust your own list.",
    ],
    pickers: [
      {
        id: "cues",
        question: "Which of these help you feel more grounded or supported?",
        options: [
          "A particular room or place",
          "Being outdoors",
          "A specific person",
          "An animal",
          "Music or a familiar sound",
          "A soft texture or blanket",
          "A warm drink",
          "A particular smell",
          "An object that matters to me",
          "A daily routine",
          "Low light",
          "Natural light",
          "Water",
          "Quiet",
        ],
        multi: true,
        allowCustom: true,
        toolkitKey: "safetyCues",
      },
    ],
    reflection: "Which of your cues is easiest to reach on a difficult day?",
    journalPrompt:
      "What helps you feel safer or more grounded in everyday life?",
  },
  {
    day: 5,
    title: "Your Window of Tolerance",
    intro: [
      "The window of tolerance is a simple way of describing how much you can handle at a given moment. It isn't a test or a diagnosis.",
      "Within my window: I can think, feel and respond.",
      "Above my window: I may feel activated, panicked, angry or overwhelmed.",
      "Below my window: I may feel numb, shut down, disconnected or exhausted.",
      "Your window widens and narrows depending on sleep, stress, support and a hundred other things. That's normal.",
    ],
    pickers: [
      {
        id: "window-now",
        question: "Where would you place yourself today?",
        options: [
          "Within my window",
          "A little above my window",
          "Well above my window",
          "A little below my window",
          "Well below my window",
          "I'm not sure",
        ],
      },
      {
        id: "window-narrow",
        question: "What tends to narrow your window?",
        options: PROFILE_HARDER,
        multi: true,
        allowCustom: true,
      },
    ],
    reflection: "What tends to widen your window, even slightly?",
    tools: [
      {
        to: "/learn/window-of-tolerance",
        label: "Window of Tolerance",
        desc: "A fuller explainer in the Learn Hub.",
      },
    ],
  },
  {
    day: 6,
    title: "Orient to the Present",
    intro: [
      "Orienting means letting your senses take in where you actually are right now. It's one of the simplest ways to support a sense of presence.",
      "Go slowly. You can stop after any step.",
    ],
    practice: {
      title: "Slow orienting",
      minutes: "2–4 minutes",
      purpose: "Let your eyes and senses take in your current surroundings.",
      steps: [
        "Look around the room slowly, letting your eyes move at their own pace.",
        "Notice 5 things you can see.",
        "Notice 4 things you can physically feel.",
        "Notice 3 things you can hear.",
        "Pause. Continue if you'd like to, or stop here.",
      ],
      tips: [
        "Turning your head slowly is part of the practice.",
        "If the room feels like too much, orient to one corner of it.",
      ],
      gentler: "Name three things you can see, then stop.",
    },
    reflection: "Did anything shift, even slightly, while you looked around?",
    reflectionOptions: ["Yes", "A little", "No", "I'm not sure"],
    tools: [
      {
        to: "/grounding",
        label: "Grounding tools",
        desc: "More orienting and grounding practices whenever you need them.",
      },
    ],
  },
  {
    day: 7,
    title: "My Nervous System Patterns",
    intro: [
      "A quiet look back over your first week. Nothing here needs a tidy answer.",
    ],
    pickers: [
      {
        id: "activators",
        question: "What situations tend to activate you?",
        options: PROFILE_HARDER,
        multi: true,
        allowCustom: true,
      },
      {
        id: "shutdown",
        question: "What situations tend to make you shut down?",
        options: [
          "Conflict",
          "Being criticised",
          "Too much noise",
          "Too many demands",
          "Feeling unseen",
          "Long days",
          "Being alone too long",
          "I'm not sure",
        ],
        multi: true,
        allowCustom: true,
      },
    ],
    reflection:
      "When do you feel most like yourself? And what helped you feel more present this week?",
    journalPrompt:
      "What patterns have you noticed in your nervous system this week?",
  },

  /* --------------------- Stage 2 — Move & Regulate --------------------- */
  {
    day: 8,
    title: "Micro-Movement Exploration",
    intro: [
      "This week is body-based. Every practice can be made smaller, slower or skipped entirely.",
      "Today's movements are tiny — just enough to feel. Less really is enough.",
    ],
    practice: {
      title: "Micro-Movement Exploration",
      minutes: "1–3 minutes",
      purpose:
        "Build awareness of sensation without needing effort or strenuous movement.",
      steps: [
        "Find a comfortable position, seated or lying down.",
        "Bring attention to one small area — fingers, shoulders, neck or feet.",
        "Begin making very small movements, just enough to feel them.",
        "Move slowly, noticing any sensation that arises.",
        "There's no need to stretch or push the movement.",
        "Continue for 1–3 minutes, then rest or move to another area if you'd like.",
      ],
      tips: [
        "Keep movements subtle and relaxed.",
        "Less movement is often more supportive.",
        "Pause whenever stillness feels better.",
      ],
      gentler: "Move one finger, slowly, five times. That counts.",
    },
    reflection: "What did you notice when you made the movement smaller?",
  },
  {
    day: 9,
    title: "Seated Body Sway",
    intro: [
      "Slow, repetitive swaying can feel comforting to some people and not to others. Both are useful information.",
    ],
    practice: {
      title: "Seated Body Sway",
      minutes: "2–4 minutes",
      purpose: "Use slow, rhythmic motion to support grounding and ease.",
      steps: [
        "Sit comfortably in a chair or on the floor, with your feet supported if possible.",
        "Let your spine be upright yet relaxed.",
        "Begin gently swaying side to side, noticing the shift of weight through your body.",
        "If comfortable, explore small, slow forward-and-back swaying.",
        "Keep the motion smooth, letting your breath stay natural.",
        "Continue for 2–4 minutes, or stop whenever stillness feels supportive.",
      ],
      tips: [
        "Stay within a comfortable range.",
        "Let the rhythm feel natural rather than forced.",
      ],
      gentler: "Sway once or twice, then rest and simply notice.",
    },
    reflection: "Did the rhythm feel soothing, neutral or uncomfortable?",
    reflectionOptions: [
      "Soothing",
      "Neutral",
      "Uncomfortable",
      "I'm not sure",
    ],
  },
  {
    day: 10,
    title: "Shoulder Rolls & Drops",
    intro: [
      "Shoulders, jaw and upper back often carry the day's holding. Slow rolling brings a little awareness there.",
    ],
    practice: {
      title: "Shoulder Rolls & Drops",
      minutes: "1–3 minutes",
      purpose: "Bring gentle awareness to holding in the shoulders and upper back.",
      steps: [
        "Sit or stand comfortably, arms resting naturally.",
        "Slowly lift your shoulders towards your ears.",
        "Gently roll them backwards in a smooth circle.",
        "Let them softly drop as you complete the circle.",
        "Repeat slowly several times, changing direction if that feels comfortable.",
        "Notice your jaw as you go — it can soften too.",
      ],
      tips: [
        "Avoid forcing the shoulders or lifting too high.",
        "Smaller circles are often more soothing.",
      ],
      seatedAlternative: "This practice works exactly the same seated.",
      gentler: "Lift and drop your shoulders once, slowly.",
    },
    reflection: "Where were you holding tension before you started?",
  },
  {
    day: 11,
    title: "Slow Joint Circles",
    intro: [
      "Predictable, rhythmic movement gives the body something steady to follow. Choose only the joints that feel comfortable today.",
    ],
    practice: {
      title: "Slow Joint Circles",
      minutes: "2–5 minutes",
      purpose: "Support comfort and ease through smooth, unhurried movement.",
      steps: [
        "Find a comfortable position — seated, standing or lying down.",
        "Choose one joint to begin with: wrists, ankles, shoulders or neck.",
        "Move the joint slowly in small, gentle circles.",
        "Keep the movement smooth, without forcing range or speed.",
        "Pause and notice how the joint feels.",
        "Change direction or move to another joint if you'd like.",
      ],
      tips: [
        "Move within a comfortable range only.",
        "Smaller circles are often more soothing.",
      ],
      gentler: "Circle your wrists only, five slow circles each way.",
    },
    pickers: [
      {
        id: "easiest-joint",
        question: "Which movement felt easiest for your body today?",
        options: ["Wrists", "Ankles", "Shoulders", "Neck", "None of them", "I'm not sure"],
        allowCustom: true,
      },
    ],
    reflection: "What did you notice as you slowed the circles down?",
  },
  {
    day: 12,
    title: "Intuitive Stretch Flow",
    intro: [
      "Today your body chooses. There are no set positions to reach and nothing to push through.",
      "Move toward what feels supportive. Comfort matters more than range. Stop whenever your body asks for rest.",
    ],
    practice: {
      title: "Intuitive Stretch Flow",
      minutes: "2–5 minutes",
      purpose:
        "Let comfort and sensation guide the stretch rather than a fixed shape.",
      steps: [
        "Begin standing, seated or lying down — whatever feels most supported.",
        "Notice areas that feel tight or restricted, without judging them.",
        "Stretch gently toward what feels supportive, letting your body lead.",
        "Hold each stretch briefly, releasing as soon as your body asks for rest.",
        "Move slowly between stretches, keeping your breath easy.",
        "Continue for a few minutes, stopping whenever you'd like.",
      ],
      tips: [
        "Stretch only to a comfortable level.",
        "Avoid holding tension or forcing a shape.",
      ],
      seatedAlternative:
        "Seated stretches — arms, neck, side body — work just as well.",
      gentler: "Stretch one arm slowly overhead, then rest.",
    },
    reflection: "What did your body seem to be asking for today?",
  },
  {
    day: 13,
    title: "Grounded Standing Flow",
    intro: [
      "Feeling the feet on the floor gives steady, predictable input. A seated version is right below if standing isn't available or comfortable today.",
    ],
    practice: {
      title: "Grounded Standing Flow",
      minutes: "2–4 minutes",
      purpose: "Support stability and a sense of contact with the ground.",
      steps: [
        "Stand comfortably with your feet about hip-width apart.",
        "Let your weight settle evenly through both feet.",
        "Notice the sensation of your feet meeting the ground.",
        "Gently shift your weight from one foot to the other.",
        "Add small, slow movements if you'd like — soft knee bends or gentle swaying.",
        "Pause in stillness whenever it feels supportive.",
      ],
      tips: [
        "Use a chair or wall for support if needed.",
        "Small movements are enough. Stillness is welcome.",
      ],
      seatedAlternative:
        "Seated: press your feet gently into the floor, one at a time, and notice the contact. Rock your weight slightly from hip to hip.",
      gentler: "Simply stand or sit and feel both feet on the floor.",
    },
    reflection: "What helped you feel most connected to the ground?",
  },
  {
    day: 14,
    title: "Authentic Free Movement",
    intro: [
      "There is no correct way to move. Small movements count. Stillness is also allowed.",
      "Nobody is watching, and there's nothing to perform.",
    ],
    practice: {
      title: "Authentic Free Movement",
      minutes: "2–5 minutes",
      purpose:
        "Let movement arise naturally rather than following instructions or set shapes.",
      steps: [
        "Find a comfortable position — standing, seated or lying down.",
        "Take a few moments to notice your breath and how your body feels.",
        "Allow small movements to begin naturally, without planning them.",
        "Move slowly, following what feels supportive in the moment.",
        "There's no goal — simply notice sensations as they arise.",
        "Continue for a few minutes, or stop sooner if you'd like.",
      ],
      tips: [
        "Keep movements slow and relaxed.",
        "Pause any time your body asks for stillness.",
      ],
      gentler: "Let one hand move however it wants for thirty seconds.",
    },
    reflection:
      "What did your body want to do when you stopped trying to perform the exercise correctly?",
    journalPrompt:
      "What did you notice when you allowed your body to move without trying to do it correctly?",
  },

  /* ------------------- Stage 3 — Find What Helps You ------------------- */
  {
    day: 15,
    title: "The Longer Exhale",
    intro: [
      "Breathing practices suit some people and not others, and rigid counts don't fit everyone. Today is an invitation, not a formula.",
      "If focusing on breath feels uncomfortable, you can skip straight to grounding or sound instead.",
    ],
    practice: {
      title: "A slightly longer exhale",
      minutes: "2–4 minutes",
      purpose: "Explore whether a longer out-breath feels settling for you.",
      steps: [
        "Sit or lie somewhere comfortable.",
        "Breathe in at a pace that feels natural — no need to make it deep.",
        "Let the exhale become slightly longer, if that feels comfortable.",
        "If counting helps, you might try in for 4 and out for 6 — but only if it feels easy.",
        "Let the breath return to normal whenever you'd like.",
      ],
      tips: [
        "Never force or hold the breath.",
        "Dizziness is a signal to return to normal breathing.",
      ],
      gentler: "Take one slow out-breath, then breathe normally.",
    },
    reflection: "Did the longer exhale feel settling, neutral or unpleasant?",
    reflectionOptions: ["Settling", "Neutral", "Unpleasant", "I'm not sure"],
    tools: [
      {
        to: "/breathing",
        label: "Breathing tools",
        desc: "Guided paced breathing you can return to any time.",
      },
    ],
  },
  {
    day: 16,
    title: "Humming Breath",
    intro: [
      "Humming adds a soft vibration to the out-breath. Some people find it comforting; some find it irritating. Both are fine.",
    ],
    practice: {
      title: "Humming Breath Practice",
      minutes: "2–4 minutes",
      purpose: "Pair slow breathing with gentle vibration in the throat and chest.",
      steps: [
        "Find a comfortable seated or lying position.",
        "Let your shoulders, jaw and face relax.",
        "Inhale gently through your nose at a natural pace.",
        "As you slowly exhale, make a soft humming sound.",
        "Keep the hum low and comfortable — no effort needed.",
        "You might place a hand on your chest or belly to notice the vibration.",
      ],
      tips: [
        "If humming feels uncomfortable or tiring, return to quiet breathing.",
        "You can stop for now at any point.",
      ],
      gentler: "One quiet hum on a single out-breath, then rest.",
    },
    reflection:
      "What did you notice about the vibration in your chest, throat or face?",
  },
  {
    day: 17,
    title: "Butterfly Hug & Bilateral Regulation",
    intro: [
      "Slow alternating taps — left, right, left, right — are calming for some people. This one is entirely optional.",
      "Keep the pace very gentle and stop immediately if anything feels like too much.",
    ],
    practice: {
      title: "Butterfly Hug",
      minutes: "1–3 minutes",
      purpose: "Explore slow, alternating input as a way of settling.",
      steps: [
        "Cross your arms over your chest, hands resting near your shoulders.",
        "Tap slowly and alternately — left, right, left, right.",
        "Keep the pace slow and the touch light.",
        "Stop whenever you'd like. There's no minimum.",
      ],
      tips: [
        "If touch isn't right today, you can tap your thighs instead, or skip.",
        "Slower is usually gentler than faster.",
      ],
      gentler: "Ten slow alternating taps, then stop.",
    },
    tools: [
      {
        to: "/bilateral",
        label: "Bilateral Stimulation tool",
        desc: "Recovery Trauma™ guided alternating sound and visuals.",
      },
    ],
    reflection: "Did the alternating rhythm feel supportive today?",
    reflectionOptions: ["Yes", "Neutral", "Not for me", "I'm not sure"],
  },
  {
    day: 18,
    title: "Sound for Regulation",
    intro: [
      "Sound is one of the easiest tools to reach for when other things feel like too much.",
      "Open the Calming Sounds library, try two or three, and notice which ones your body prefers.",
    ],
    tools: [
      {
        to: "/sounds",
        label: "Calming Sounds",
        desc: "White noise, brown noise, rain, ocean, nature and sound baths.",
      },
    ],
    pickers: [
      {
        id: "sounds",
        question: "Which sounds make your body feel more settled?",
        options: [
          "White noise",
          "Brown noise",
          "Rain",
          "Ocean",
          "Water stream",
          "Nature / forest",
          "Sound bath",
          "Quiet — no sound",
        ],
        multi: true,
        allowCustom: true,
        toolkitKey: "sounds",
      },
    ],
    reflection: "Where in your body did you notice the sound landing?",
  },
  {
    day: 19,
    title: "Gentle Touch & Self-Soothing",
    intro: [
      "Supportive touch helps some people feel steadier. For others, touch is uncomfortable — and that's completely valid.",
      "Every option below is optional, including all of them.",
    ],
    pickers: [
      {
        id: "touch",
        question: "Which of these would you like to try, if any?",
        options: [
          "Hand over heart",
          "Hand on chest",
          "Hand on stomach",
          "Holding my own arms",
          "A weighted blanket",
          "A comfortable object",
          "I'd rather use a non-touch practice",
        ],
        multi: true,
        allowCustom: true,
      },
    ],
    optional:
      "Prefer no touch? Try orienting around the room, listening to a familiar sound, or resting your eyes on something pleasant instead.",
    reflection: "What felt supportive, and what didn't?",
    tools: [
      {
        to: "/somatic/hand-on-heart",
        label: "Hand on Heart",
        desc: "A guided version of this practice.",
      },
    ],
  },
  {
    day: 20,
    title: "Movement When You Feel Stuck",
    intro: [
      "Sometimes the body feels heavy, stuck or far away. There's nothing to snap out of.",
      "If movement feels supportive, you might explore one of these gentle options. If it doesn't, resting is a legitimate choice.",
    ],
    pickers: [
      {
        id: "stuck-tools",
        question: "Which feels most possible right now?",
        options: [
          "Micro-movements",
          "Shoulder rolls",
          "Swaying",
          "Weight shifts",
          "A gentle stretch",
          "Walking slowly around the room",
          "Standing up and sitting down again",
          "Resting instead",
        ],
        multi: true,
        allowCustom: true,
        toolkitKey: "whenDisconnected",
      },
    ],
    reflection: "What was it like to choose for yourself rather than push?",
  },
  {
    day: 21,
    title: "Build Your Regulation Menu",
    intro: [
      "This is one of the most useful days. You're building a short personal menu you can reach for without having to think.",
      "Nothing here is fixed — you can edit it any time.",
    ],
    pickers: [
      {
        id: "menu-activated",
        question: "When I feel activated, my useful tools might be…",
        options: [
          "Grounding",
          "Slower breathing",
          "Sound",
          "Walking",
          "Stretching",
          "Orienting",
          "Cold water on my hands",
          "Writing it down",
        ],
        multi: true,
        allowCustom: true,
        toolkitKey: "whenActivated",
      },
      {
        id: "menu-numb",
        question: "When I feel numb or shut down, my useful tools might be…",
        options: [
          "Gentle movement",
          "Warmth",
          "Music",
          "Light",
          "Safe connection",
          "Sensory grounding",
          "A warm drink",
          "Stepping outside",
        ],
        multi: true,
        allowCustom: true,
        toolkitKey: "whenDisconnected",
      },
      {
        id: "menu-bed",
        question: "Before bed, what helps me settle?",
        options: [
          "Sleep sounds",
          "Slow breathing",
          "Gentle stretching",
          "Journaling",
          "Dimming the lights",
          "A body scan",
          "Reading",
          "A warm shower",
        ],
        multi: true,
        allowCustom: true,
        toolkitKey: "beforeBed",
      },
    ],
    journalPrompt:
      "Which regulation tools seem to work best for you in different states?",
  },

  /* ------------------ Stage 4 — Bring It Into Real Life ------------------ */
  {
    day: 22,
    title: "When You Feel Triggered",
    intro: [
      "A simple sequence you can use in the moment:",
      "PAUSE → NOTICE → GROUND → CHOOSE.",
      "Pause what you're doing. Notice what's happening in your body. Ground with one small practice. Then choose what you want to do next.",
      "You don't need to revisit any detailed memories here — only what you noticed.",
    ],
    pickers: [
      {
        id: "trigger-body",
        question: "When something shifts, what does your body do first?",
        options: [
          "Heart races",
          "Chest tightens",
          "Stomach drops",
          "Jaw clenches",
          "Shoulders rise",
          "I go quiet",
          "I go blank",
          "I want to leave",
          "I'm not sure",
        ],
        multi: true,
        allowCustom: true,
      },
    ],
    reflection:
      "What happened just before you last noticed the shift — and what might help you feel more present right now?",
    tools: [
      { to: "/grounding", label: "Grounding", desc: "One-minute grounding practices." },
      { to: "/quick-resets", label: "Quick Resets", desc: "Short practices for difficult moments." },
    ],
  },
  {
    day: 23,
    title: "When You Feel Numb or Disconnected",
    intro: [
      "Disconnection is a protective response, not a failure. Nobody needs to snap out of it.",
      "Small, sensory, low-effort things are often the most possible.",
    ],
    pickers: [
      {
        id: "numb-tools",
        question: "Which of these feels doable when you're disconnected?",
        options: [
          "Notice my feet",
          "Hold a warm drink",
          "Look around the room",
          "Listen to a familiar sound",
          "Make one small movement",
          "Name today's date",
          "Notice one colour in the room",
          "Open a window",
        ],
        multi: true,
        allowCustom: true,
        toolkitKey: "whenDisconnected",
      },
    ],
    reflection: "What tends to help you reconnect, even a little?",
  },
  {
    day: 24,
    title: "Regulate Before You Respond",
    intro: [
      "When something lands hard — a message, a comment, a conversation — there's often a gap between the feeling and the response.",
      "Pause. Notice your body. Choose one regulation tool. Then decide whether you want to respond at all.",
      "This can be especially relevant to conflict, texts and messages, reassurance-seeking, arguments and difficult conversations.",
    ],
    pickers: [
      {
        id: "pause-tool",
        question: "Which tool would you most likely reach for in that gap?",
        options: [
          "One slow out-breath",
          "Feet on the floor",
          "Step outside",
          "Put the phone down for ten minutes",
          "Write the reply and don't send it yet",
          "Ask for time",
          "Sound",
        ],
        multi: true,
        allowCustom: true,
        toolkitKey: "whenActivated",
      },
    ],
    reflection: "What usually makes the pause hardest for you?",
  },
  {
    day: 25,
    title: "When Your Mind Won't Stop",
    intro: [
      "Overthinking and rumination often ease more through the senses than through more thinking.",
      "Try one of these and notice what happens — orienting, sensory grounding, sound, paced breathing, writing thoughts down, or movement.",
    ],
    pickers: [
      {
        id: "rumination",
        question: "Which of these helped, if any?",
        options: [
          "Orienting",
          "Sensory grounding",
          "Sound",
          "Paced breathing",
          "Writing thoughts down",
          "Movement",
          "None of them today",
        ],
        multi: true,
        allowCustom: true,
      },
    ],
    journalPrompt: "What's running through my mind right now?",
    reflection: "What does your mind seem to be trying to protect you from?",
  },
  {
    day: 26,
    title: "Rest Without Guilt",
    intro: [
      "Slowing down can feel unfamiliar or even unsafe, especially if staying busy has been protective.",
      "There's no pressure to relax successfully today.",
    ],
    pickers: [
      {
        id: "rest-feeling",
        question: "When you slow down, rest feels…",
        options: [
          "Safe",
          "Uncomfortable",
          "Unfamiliar",
          "Guilty",
          "Boring",
          "A relief",
          "I'm not sure",
        ],
        multi: true,
        allowCustom: true,
      },
    ],
    reflection: "What would compassionate rest look like today?",
    journalPrompt: "What beliefs or feelings come up when you allow yourself to rest?",
  },
  {
    day: 27,
    title: "My Morning Regulation Ritual",
    intro: [
      "Choose two to four small things for the start of the day. Short is better than ambitious.",
    ],
    pickers: [
      {
        id: "morning",
        question: "My morning ritual might include…",
        options: [
          "A quick check-in",
          "A gentle stretch",
          "Slow breathing",
          "Morning movement",
          "Sound or music",
          "Grounding",
          "Setting one intention",
          "Daylight",
          "Water first",
        ],
        multi: true,
        allowCustom: true,
        toolkitKey: "morning",
      },
    ],
    reflection: "What would make this ritual realistic on a hard morning?",
    tools: [
      { to: "/check-in", label: "Daily Check-In", desc: "A one-minute morning check-in." },
    ],
  },
  {
    day: 28,
    title: "My Evening Wind-Down",
    intro: [
      "Evenings often set the tone for sleep. Pick a small handful of things that help you dim the day.",
    ],
    pickers: [
      {
        id: "evening",
        question: "My evening wind-down might include…",
        options: [
          "Sleep sounds",
          "Slow breathing",
          "Gentle stretching",
          "Journaling",
          "Dimming stimulation",
          "A body scan",
          "Grounding",
          "A calming routine",
        ],
        multi: true,
        allowCustom: true,
        toolkitKey: "evening",
      },
    ],
    tools: [
      { to: "/sleep", label: "Sleep Support", desc: "Wind-down practices and sleep sounds." },
      { to: "/sleep-dreams", label: "Sleep & Dreams", desc: "Dream journalling and night-time tools." },
    ],
    reflection: "What tends to get in the way of your wind-down?",
  },
  {
    day: 29,
    title: "Build My Nervous System Toolkit",
    intro: [
      "Look back over the last 28 days and choose what actually helped you — not what sounds like it should.",
      "Anything you've already saved is pre-filled, and you can change all of it.",
    ],
    pickers: [
      {
        id: "best-grounding",
        question: "My best grounding tool",
        options: [
          "Orienting",
          "5-4-3-2-1 senses",
          "Feet on the floor",
          "Cold water",
          "Naming the date and place",
          "Holding an object",
        ],
        allowCustom: true,
        toolkitKey: "grounding",
      },
      {
        id: "best-movement",
        question: "My best movement practice",
        options: [
          "Micro-movements",
          "Seated body sway",
          "Shoulder rolls & drops",
          "Slow joint circles",
          "Intuitive stretch flow",
          "Grounded standing flow",
          "Authentic free movement",
        ],
        allowCustom: true,
        toolkitKey: "movement",
      },
      {
        id: "best-breathing",
        question: "My best breathing practice",
        options: [
          "Longer exhale",
          "Humming breath",
          "Soft belly breathing",
          "Box-style gentle breathing",
          "Just normal breathing",
        ],
        allowCustom: true,
        toolkitKey: "breathing",
      },
      {
        id: "best-sound",
        question: "My favourite sound",
        options: [
          "White noise",
          "Brown noise",
          "Rain",
          "Ocean",
          "Water stream",
          "Nature / forest",
          "Sound bath",
          "Quiet",
        ],
        allowCustom: true,
        toolkitKey: "sounds",
      },
      {
        id: "goto",
        question: "My 5 go-to practices",
        note: "Choose up to five — the ones you'd want on your worst day.",
        options: [
          "Orienting",
          "Feet on the floor",
          "Longer exhale",
          "Humming breath",
          "Butterfly hug",
          "Seated body sway",
          "Shoulder rolls",
          "Micro-movements",
          "Calming sounds",
          "Warm drink",
          "Step outside",
          "Journalling",
          "Message someone I trust",
        ],
        multi: true,
        allowCustom: true,
        toolkitKey: "goTo",
      },
    ],
  },
  {
    day: 30,
    title: "What I've Learned About Myself",
    intro: [
      "30 days of learning your nervous system. Not a finish line, and nothing that needed to be fixed.",
      "Take these last few questions slowly.",
    ],
    pickers: [
      {
        id: "early-activation",
        question: "My early signs of activation",
        options: [
          "Racing thoughts",
          "Tight chest",
          "Clenched jaw",
          "Restlessness",
          "Irritability",
          "Talking faster",
          "Scrolling",
          "Snapping at people",
        ],
        multi: true,
        allowCustom: true,
      },
      {
        id: "early-shutdown",
        question: "My early signs of shutdown",
        options: [
          "Heaviness",
          "Going quiet",
          "Feeling far away",
          "Cancelling plans",
          "Sleeping more",
          "Losing interest",
          "Blank mind",
        ],
        multi: true,
        allowCustom: true,
      },
      {
        id: "not-for-me",
        question: "Which practices weren't right for me?",
        options: [
          "Breathing practices",
          "Humming",
          "Touch-based practices",
          "Standing movement",
          "Body scanning",
          "Sound",
          "None — they were all okay",
        ],
        multi: true,
        allowCustom: true,
      },
      {
        id: "continue",
        question: "What would I like to continue practising?",
        options: [
          "Daily check-in",
          "Morning ritual",
          "Evening wind-down",
          "Movement",
          "Grounding",
          "Sound",
          "Journalling",
        ],
        multi: true,
        allowCustom: true,
      },
    ],
    reflection:
      "What do you understand about yourself now that you hadn't noticed before?",
    journalPrompt:
      "What have these 30 days taught you about what your body and nervous system need?",
  },
];

export function dayContent(day: number): Day {
  return DAYS.find((d) => d.day === day) ?? DAYS[0];
}

/* ------------------------------ Member state ------------------------------ */

export interface DayRecord {
  completedAt?: string;
  checkInState?: string;
  checkInBody?: string[];
  capacity?: string;
  after?: string;
  keep?: string;
  answers?: Record<string, string[]>;
  reflection?: string;
}

export interface NsrState {
  startedAt?: string;
  profile?: {
    stressResponses?: string[];
    bodyAreas?: string[];
    helps?: string[];
    harder?: string[];
    hope?: string;
    skipped?: boolean;
    completedAt?: string;
  };
  days?: Record<string, DayRecord>;
  savedTools?: string[];
  toolkit?: Partial<Record<ToolkitKey, string[]>>;
  toolkitSavedAt?: string;
}

export function completedDays(state: NsrState): number[] {
  return Object.entries(state.days ?? {})
    .filter(([, v]) => !!v?.completedAt)
    .map(([k]) => Number(k))
    .sort((a, b) => a - b);
}

export function currentDay(state: NsrState): number {
  const done = completedDays(state);
  for (let d = 1; d <= 30; d += 1) if (!done.includes(d)) return d;
  return 30;
}

export function percentComplete(state: NsrState): number {
  return Math.round((completedDays(state).length / 30) * 100);
}

/** Tools the member actively saved, most frequently first. */
export function mostHelpfulTools(state: NsrState, limit = 6): string[] {
  const counts = new Map<string, number>();
  for (const tool of state.savedTools ?? []) {
    counts.set(tool, (counts.get(tool) ?? 0) + 1);
  }
  for (const list of Object.values(state.toolkit ?? {})) {
    for (const v of list ?? []) counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([k]) => k);
}

export const TOOLKIT_SECTIONS: { key: ToolkitKey; label: string }[] = [
  { key: "grounding", label: "My grounding tools" },
  { key: "movement", label: "My favourite somatic practices" },
  { key: "breathing", label: "My breathing tools" },
  { key: "sounds", label: "My calming sounds" },
  { key: "safetyCues", label: "My safety cues" },
  { key: "whenActivated", label: "What helps when I'm activated" },
  { key: "whenDisconnected", label: "What helps when I'm disconnected" },
  { key: "morning", label: "My morning routine" },
  { key: "evening", label: "My evening routine" },
  { key: "beforeBed", label: "What helps me settle before bed" },
  { key: "goTo", label: "My 5 go-to practices" },
];
